import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, CreditCard, Receipt } from 'lucide-react';
import { format } from 'date-fns';
import type { UserDetail } from '../types';

export function SubscriptionPanel({ detail }: { detail: UserDetail }) {
  const sub = detail.subscription;
  const stripeUrl = sub?.stripe_customer_id
    ? `https://dashboard.stripe.com/customers/${sub.stripe_customer_id}`
    : null;

  return (
    <div className="space-y-5">
      {/* Subscription card */}
      <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
        <div className="p-4 border-b border-border/60 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-muted-foreground" />
            <h4 className="text-sm font-semibold">Subscription</h4>
          </div>
          {stripeUrl && (
            <Button asChild size="sm" variant="outline" className="gap-2">
              <a href={stripeUrl} target="_blank" rel="noreferrer">
                Open in Stripe <ExternalLink className="h-3 w-3" />
              </a>
            </Button>
          )}
        </div>
        {sub ? (
          <div className="p-4 space-y-2 text-sm">
            <Row
              label="Tier"
              value={
                <Badge variant="outline" className="capitalize">
                  {sub.tier}
                </Badge>
              }
            />
            <Row
              label="Status"
              value={
                <Badge
                  variant={sub.status === 'active' ? 'default' : 'secondary'}
                  className="capitalize"
                >
                  {sub.status}
                </Badge>
              }
            />
            <Row label="Billing cycle" value={sub.billing_cycle} />
            <Row
              label="Credits remaining"
              value={
                <span className="font-mono tabular-nums">
                  {sub.credits_remaining} / {sub.credits_monthly}
                </span>
              }
            />
            <Row
              label="Period start"
              value={
                sub.current_period_start
                  ? format(new Date(sub.current_period_start), 'dd.MM.yyyy')
                  : '—'
              }
            />
            <Row
              label="Period end"
              value={
                sub.current_period_end
                  ? format(new Date(sub.current_period_end), 'dd.MM.yyyy')
                  : '—'
              }
            />
            <Row
              label="Cancel at period end"
              value={
                sub.cancel_at_period_end ? (
                  <Badge variant="outline" className="border-warning/40 text-warning bg-warning/5">
                    Yes
                  </Badge>
                ) : (
                  'No'
                )
              }
            />
            <Row
              label="Stripe customer"
              value={
                sub.stripe_customer_id ? (
                  <code className="text-xs bg-muted/60 px-1.5 py-0.5 rounded">
                    {sub.stripe_customer_id}
                  </code>
                ) : (
                  '—'
                )
              }
            />
            <Row
              label="Stripe subscription"
              value={
                sub.stripe_subscription_id ? (
                  <code className="text-xs bg-muted/60 px-1.5 py-0.5 rounded">
                    {sub.stripe_subscription_id}
                  </code>
                ) : (
                  '—'
                )
              }
            />
            {sub.scheduled_tier && (
              <Row
                label="Scheduled change"
                value={
                  <span className="text-warning">
                    {sub.scheduled_tier} ({sub.scheduled_billing_cycle}) on{' '}
                    {sub.scheduled_change_date
                      ? format(new Date(sub.scheduled_change_date), 'dd.MM.yyyy')
                      : '—'}
                  </span>
                }
              />
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground p-6 text-center">
            No subscription on record.
          </p>
        )}
      </div>

      {/* Top-ups */}
      <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
        <div className="p-4 border-b border-border/60 flex items-center gap-2">
          <Receipt className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            Top-up purchases <span className="text-muted-foreground font-normal">({detail.topups.length})</span>
          </h4>
        </div>
        {detail.topups.length === 0 ? (
          <p className="text-sm text-muted-foreground p-6 text-center">No top-ups.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wide text-muted-foreground">
                    Date
                  </th>
                  <th className="text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wide text-muted-foreground">
                    Package
                  </th>
                  <th className="text-right px-4 py-2.5 font-medium text-xs uppercase tracking-wide text-muted-foreground">
                    Credits
                  </th>
                  <th className="text-right px-4 py-2.5 font-medium text-xs uppercase tracking-wide text-muted-foreground">
                    Amount
                  </th>
                  <th className="text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wide text-muted-foreground">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {detail.topups.map((t) => (
                  <tr key={t.id} className="border-t border-border/60">
                    <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap text-xs">
                      {format(new Date(t.created_at), 'dd.MM.yy HH:mm')}
                    </td>
                    <td className="px-4 py-2.5 capitalize">{t.package}</td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums">
                      {t.credits_purchased}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums">
                      €{Number(t.amount_paid).toFixed(2)}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge
                        variant={
                          t.payment_status === 'completed' || t.payment_status === 'paid'
                            ? 'default'
                            : 'secondary'
                        }
                        className="capitalize"
                      >
                        {t.payment_status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5 border-b border-border/40 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}
